"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import { Home, MessageCircle } from "lucide-react";

import BoxReveal from "@cohub42/ui/components/magicui/box-reveal";
import { Button } from "@cohub42/ui/components/ui/button";
import { Spotlight } from "@cohub42/ui/components/ui/spotlight";

function FloatingParticle({
  delay,
  duration,
  x,
  y,
  size,
}: {
  delay: number;
  duration: number;
  x: number;
  y: number;
  size: number;
}) {
  return (
    <motion.div
      className="absolute rounded-full bg-blue-500/20 dark:bg-blue-400/10"
      style={{
        width: size,
        height: size,
        left: `${x}%`,
        top: `${y}%`,
      }}
      animate={{
        y: [0, -30, 0],
        opacity: [0.3, 0.8, 0.3],
        scale: [1, 1.2, 1],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut",
      }}
    />
  );
}

const particles = [
  { delay: 0, duration: 4, x: 10, y: 20, size: 6 },
  { delay: 1, duration: 5, x: 85, y: 15, size: 8 },
  { delay: 0.5, duration: 6, x: 70, y: 70, size: 5 },
  { delay: 2, duration: 4.5, x: 20, y: 75, size: 7 },
  { delay: 1.5, duration: 5.5, x: 50, y: 10, size: 4 },
  { delay: 0.8, duration: 4, x: 90, y: 50, size: 6 },
  { delay: 2.5, duration: 5, x: 30, y: 45, size: 5 },
  { delay: 1.2, duration: 6, x: 65, y: 85, size: 7 },
];

export default function NotFound() {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div className="bg-grid-white/[0.02] relative flex min-h-screen w-full items-center justify-center overflow-hidden bg-white/[0.96] antialiased dark:bg-black/[0.96]">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-blue-950/20 dark:via-black dark:to-purple-950/20" />

      {/* Spotlight effects */}
      <Spotlight
        className="-top-40 left-0 md:-top-20 md:left-60"
        fill="#3B82F6"
      />
      <Spotlight
        className="-top-40 right-0 md:-top-20 md:right-60"
        fill="#8B5CF6"
      />

      {/* Floating particles */}
      {mounted &&
        particles.map((particle, i) => (
          <FloatingParticle key={i} {...particle} />
        ))}

      {/* Content */}
      <div className="relative z-10 mx-auto flex max-w-2xl flex-col items-center px-4 text-center">
        {/* 404 Number */}
        <BoxReveal boxColor="transparent" duration={0.3}>
          <motion.h1
            className="select-none bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-[10rem] font-black leading-none tracking-tighter text-transparent sm:text-[12rem]"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            404
          </motion.h1>
        </BoxReveal>

        {/* Divider line */}
        <BoxReveal boxColor="transparent" duration={0.4}>
          <div className="mb-6 h-1 w-24 rounded-full bg-gradient-to-r from-blue-600 to-purple-600" />
        </BoxReveal>

        {/* Heading */}
        <BoxReveal boxColor="transparent" duration={0.5}>
          <h2 className="bg-gradient-to-b from-slate-900 to-zinc-500 bg-clip-text text-3xl font-bold text-transparent dark:from-neutral-200 dark:to-neutral-600 sm:text-4xl">
            Page Not Found
          </h2>
        </BoxReveal>

        {/* Description */}
        <BoxReveal boxColor="transparent" duration={0.6} width="100%">
          <p className="mx-auto mt-4 max-w-md text-base text-neutral-600 dark:text-neutral-400 sm:text-lg">
            The page you&apos;re looking for doesn&apos;t exist or has been
            moved. Let&apos;s get you back on track to find your perfect
            co-founder.
          </p>
        </BoxReveal>

        {/* CTA Buttons */}
        <BoxReveal boxColor="transparent" duration={0.7} width="100%">
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link href="/">
              <Button className="group relative inline-flex h-12 items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/25">
                <Home className="h-4 w-4" />
                <span className="relative z-10">Go Home</span>
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              </Button>
            </Link>

            <Link href="/contact">
              <Button
                variant="outline"
                className="inline-flex h-12 items-center gap-2 rounded-full border-neutral-300 px-8 font-medium transition-all duration-300 hover:scale-105 hover:border-blue-500/50 hover:bg-blue-50 dark:border-neutral-700 dark:hover:border-blue-400/50 dark:hover:bg-blue-950/20"
              >
                <MessageCircle className="h-4 w-4" />
                Contact Us
              </Button>
            </Link>
          </div>
        </BoxReveal>

        {/* Subtle hint */}
        <BoxReveal boxColor="transparent" duration={0.8} width="100%">
          <p className="mt-8 text-sm text-neutral-400 dark:text-neutral-500">
            Error 404 — cohub42.com
          </p>
        </BoxReveal>
      </div>
    </div>
  );
}
